import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import axios from 'axios';
import {
  FileType2,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Eye,
  Trash2,
  Copy,
  RefreshCw,
  Save,
  X,
  FileText,
  Code,
  ChevronLeft,
  ArrowLeft,
  CheckCircle,
  Clock,
  Archive,
  Lock,
  Play,
  AlertTriangle,
  Settings,
  Target,
  Building2,
  GitBranch,
  BookOpen,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  inactive: { label: 'Inactive', color: 'bg-slate-500/10 text-slate-400 border-slate-500/30', icon: Clock },
  draft: { label: 'Draft', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', icon: Clock },
  deprecated: { label: 'Deprecated', color: 'bg-red-500/10 text-red-400 border-red-500/30', icon: Archive },
  archived: { label: 'Archived', color: 'bg-slate-600/10 text-slate-500 border-slate-600/30', icon: Archive },
};

const TEMPLATE_TYPES = [
  { value: 'factual', label: 'Factual Dispute' },
  { value: 'metro2', label: 'Metro 2 Technical' },
  { value: 'data_breach', label: 'Data Breach' },
  { value: 'inquiry', label: 'Inquiry Removal' },
  { value: 'debt_validation', label: 'Debt Validation' },
  { value: 'goodwill', label: 'Goodwill Adjustment' },
  { value: 'identity_theft', label: 'Identity Theft' },
  { value: 'escalation', label: 'Escalation' },
  { value: 'response', label: 'Response' },
];

const DISPUTE_FAMILIES = [
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'personal_info', label: 'Personal Information' },
  { value: 'negative_account', label: 'Negative Account' },
  { value: 'late_payment', label: 'Late Payment' },
  { value: 'collection', label: 'Collection' },
  { value: 'chargeoff', label: 'Charge-Off' },
  { value: 'bankruptcy', label: 'Bankruptcy' },
  { value: 'metro2_violation', label: 'Metro 2 Violation' },
];

const BUREAUS = ['equifax', 'experian', 'transunion'];

const PLACEHOLDER_TAGS = [
  '{{ consumer.full_name }}',
  '{{ consumer.address_line1 }}',
  '{{ consumer.city }}',
  '{{ consumer.state }}',
  '{{ consumer.zip_code }}',
  '{{ consumer.ssn_last_four }}',
  '{{ bureau.name }}',
  '{{ bureau.address }}',
  '{{ bureau.city_state_zip }}',
  '{{ letter_date }}',
  '{{ account.creditor }}',
  '{{ account.account_number }}',
  '{{ account.balance }}',
  '{{ account.status }}',
  '{{ inquiry.company_name }}',
  '{{ inquiry.inquiry_date }}',
];


// ==================== TEMPLATE CARD ====================

const TemplateCard = ({ template, onEdit, onDelete, onPreview, onDuplicate }) => {
  const [showMenu, setShowMenu] = useState(false);
  const StatusIcon = STATUS_CONFIG[template.status]?.icon || Clock;

  return (
    <div 
      className="bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-amber-500/30 transition-all group"
      data-testid={`template-card-${template.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-600/20 flex items-center justify-center">
            <FileType2 className="text-teal-400" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-white">{template.name}</h3>
            <p className="text-xs text-slate-500 capitalize">{template.template_type?.replace(/_/g, ' ')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_CONFIG[template.status]?.color}`}>
            <StatusIcon size={12} className="inline mr-1" />
            {STATUS_CONFIG[template.status]?.label || template.status}
          </span>
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-stone-800 transition-all"
              data-testid={`template-menu-btn-${template.id}`}
            >
              <MoreVertical size={16} />
            </button>
            {showMenu && (
              <div 
                className="absolute right-0 mt-1 w-40 bg-stone-800 border border-slate-700 rounded-lg shadow-xl z-10 py-1"
                onClick={(e) => e.stopPropagation()}
                data-testid={`template-menu-dropdown-${template.id}`}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); onPreview(template); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                  data-testid={`template-preview-btn-${template.id}`}
                >
                  <Eye size={14} /> Preview
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onEdit(template); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                  data-testid={`template-edit-btn-${template.id}`}
                >
                  <Edit size={14} /> Edit
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDuplicate(template); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                  data-testid={`template-duplicate-btn-${template.id}`}
                >
                  <Copy size={14} /> Duplicate
                </button>
                <hr className="my-1 border-slate-700" />
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(template); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10"
                  data-testid={`template-delete-btn-${template.id}`}
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <p className="text-sm text-slate-400 mb-4 line-clamp-2">{template.description || 'No description'}</p>
      
      <div className="flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-3">
          {template.method_key && (
            <span className="px-2 py-1 rounded bg-amber-500/10 text-amber-400">
              {template.method_key}
            </span>
          )}
          {template.version && (
            <span className="px-2 py-1 rounded bg-slate-700/50 text-slate-400">
              v{template.version}
            </span>
          )}
        </div>
        <Button
          onClick={() => onEdit(template)}
          variant="ghost"
          size="sm"
          className="opacity-0 group-hover:opacity-100 transition-opacity"
        >
          Open Editor →
        </Button>
      </div>
    </div>
  );
};


// ==================== FULL PAGE TEMPLATE EDITOR ====================

const TemplateEditor = ({ template, onSave, onClose, authToken }) => {
  const [formData, setFormData] = useState({
    name: '',
    template_type: 'factual',
    dispute_family: 'negative_account',
    method_key: '',
    round: 1,
    content: '',
    description: '',
    status: 'draft',
    version: 1,
    applies_to: [],
    default_for: [],
    selectable_for: [],
    blocked_for: [],
    bureau_targeting_mode: 'reported_only',
    letter_count_rule: 'per_bureau_reported',
    output_format: 'flat_legal',
    fallback_priority: 50,
    ...template
  });
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [globalPolicy, setGlobalPolicy] = useState(null);

  // Fetch global policy
  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const response = await axios.get(`${API}/admin-rules/global-policy`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        setGlobalPolicy(response.data.policy);
      } catch (error) {
        console.error('Error fetching policy:', error);
      }
    };
    fetchPolicy();
  }, [authToken]);

  // Generate preview
  useEffect(() => {
    if (formData.content) {
      // Simple preview - replace placeholders with sample data
      let preview = formData.content
        .replace(/\{\{\s*consumer\.full_name\s*\}\}/g, 'JOHN DOE')
        .replace(/\{\{\s*consumer\.address_line1\s*\}\}/g, '123 Main Street')
        .replace(/\{\{\s*consumer\.city\s*\}\}/g, 'Anytown')
        .replace(/\{\{\s*consumer\.state\s*\}\}/g, 'CA')
        .replace(/\{\{\s*consumer\.zip_code\s*\}\}/g, '12345')
        .replace(/\{\{\s*bureau\.name\s*\}\}/g, 'Equifax Information Services, LLC')
        .replace(/\{\{\s*bureau\.address\s*\}\}/g, 'P.O. Box 740256')
        .replace(/\{\{\s*bureau\.city_state_zip\s*\}\}/g, 'Atlanta, GA 30374-0256')
        .replace(/\{\{\s*letter_date\s*\}\}/g, new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
      setPreviewHtml(preview);
    }
  }, [formData.content]);

  const handleSave = async (status = 'draft') => {
    setSaving(true);
    try {
      const saveData = { ...formData, status };
      
      // Enforce flat output for factual types
      if (['factual', 'metro2', 'inquiry', 'data_breach'].includes(formData.template_type) && globalPolicy?.locked) {
        saveData.output_format = 'flat_legal';
      }
      
      if (template?.id) {
        await adminOpsService.updateTemplate(template.id, saveData);
        toast.success(`Template ${status === 'active' ? 'published' : 'saved'}`);
      } else {
        await adminOpsService.createTemplate(saveData);
        toast.success('Template created');
      }
      onSave();
    } catch (error) {
      toast.error('Failed to save template');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const insertPlaceholder = (placeholder) => {
    setFormData(prev => ({
      ...prev,
      content: prev.content + placeholder
    }));
  };

  const isFactualType = ['factual', 'metro2', 'inquiry', 'data_breach'].includes(formData.template_type);
  const flatOutputEnforced = isFactualType && globalPolicy?.locked;

  return (
    <div className="fixed inset-0 bg-[#0B1F18] z-50 flex flex-col" data-testid="template-editor">
      {/* Top Bar */}
      <div className="h-16 border-b border-stone-800 bg-stone-900 flex items-center justify-between px-6 flex-shrink-0">
        <div className="flex items-center gap-4">
          <Button onClick={onClose} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
            <ArrowLeft size={18} className="mr-2" /> Back to Templates
          </Button>
          <div className="h-6 w-px bg-stone-700" />
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Template Name"
            className="w-72 bg-stone-800 border-stone-700 text-lg font-semibold"
          />
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => handleSave('draft')} variant="outline" disabled={saving} className="border-slate-700">
            <Save size={16} className="mr-2" /> Save Draft
          </Button>
          <Button onClick={() => handleSave('active')} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            <CheckCircle size={16} className="mr-2" /> Publish
          </Button>
          <Button onClick={onClose} variant="ghost" size="sm" className="text-slate-400">
            <X size={18} />
          </Button>
        </div>
      </div>

      {/* Main Content - 3 Panel Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT PANEL - Metadata */}
        <div className="w-80 border-r border-stone-800 bg-stone-900/50 overflow-y-auto p-4 flex-shrink-0">
          <div className="space-y-6">
            {/* Flat Output Policy Badge */}
            {flatOutputEnforced && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
                  <Lock size={14} />
                  <span>Flat Output Enforced</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Global policy requires plain legal format
                </p>
              </div>
            )}

            {/* Basic Info */}
            <div>
              <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <FileText size={14} /> Basic Information
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Template Type</label>
                  <Select value={formData.template_type} onValueChange={(v) => setFormData({ ...formData, template_type: v })}>
                    <SelectTrigger className="bg-stone-800 border-stone-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Dispute Family</label>
                  <Select value={formData.dispute_family} onValueChange={(v) => setFormData({ ...formData, dispute_family: v })}>
                    <SelectTrigger className="bg-stone-800 border-stone-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DISPUTE_FAMILIES.map((f) => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Method Key</label>
                  <Input
                    value={formData.method_key}
                    onChange={(e) => setFormData({ ...formData, method_key: e.target.value })}
                    placeholder="e.g., factual_inaccuracy"
                    className="bg-stone-800 border-stone-700 font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Round</label>
                  <Select value={String(formData.round)} onValueChange={(v) => setFormData({ ...formData, round: parseInt(v) })}>
                    <SelectTrigger className="bg-stone-800 border-stone-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map((r) => (
                        <SelectItem key={r} value={String(r)}>Round {r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Applicability */}
            <div>
              <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <Target size={14} /> Applicability
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Applies To Bureaus</label>
                  <div className="flex flex-wrap gap-2">
                    {BUREAUS.map((bureau) => (
                      <button
                        key={bureau}
                        onClick={() => {
                          const applies = formData.applies_to || [];
                          setFormData({
                            ...formData,
                            applies_to: applies.includes(bureau)
                              ? applies.filter(b => b !== bureau)
                              : [...applies, bureau]
                          });
                        }}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-all capitalize ${
                          (formData.applies_to || []).includes(bureau)
                            ? 'bg-teal-500/20 text-teal-400 border-teal-500/30'
                            : 'bg-stone-800 text-slate-400 border-stone-700 hover:border-slate-600'
                        }`}
                      >
                        {bureau}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Output Rules */}
            <div>
              <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <Settings size={14} /> Output Rules
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Output Format</label>
                  <Select 
                    value={formData.output_format} 
                    onValueChange={(v) => setFormData({ ...formData, output_format: v })}
                    disabled={flatOutputEnforced}
                  >
                    <SelectTrigger className={`bg-stone-800 border-stone-700 ${flatOutputEnforced ? 'opacity-50' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat_legal">Flat / Plain Legal</SelectItem>
                      <SelectItem value="styled" disabled={flatOutputEnforced}>Styled (Disabled)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Bureau Targeting</label>
                  <Select value={formData.bureau_targeting_mode} onValueChange={(v) => setFormData({ ...formData, bureau_targeting_mode: v })}>
                    <SelectTrigger className="bg-stone-800 border-stone-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reported_only">Reported Only</SelectItem>
                      <SelectItem value="all_bureaus">All Bureaus</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Letter Count Rule</label>
                  <Select value={formData.letter_count_rule} onValueChange={(v) => setFormData({ ...formData, letter_count_rule: v })}>
                    <SelectTrigger className="bg-stone-800 border-stone-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_bureau_reported">Per Bureau Reported</SelectItem>
                      <SelectItem value="per_bureau_all">Per Bureau (All 3)</SelectItem>
                      <SelectItem value="combined">Combined Single</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Status & Version */}
            <div>
              <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <CheckCircle size={14} /> Status
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Current Status</label>
                  <span className={`inline-flex px-3 py-1.5 text-sm rounded-full border ${STATUS_CONFIG[formData.status]?.color}`}>
                    {STATUS_CONFIG[formData.status]?.label || formData.status}
                  </span>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Version</label>
                  <span className="text-sm text-white">v{formData.version}</span>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Fallback Priority</label>
                  <Input
                    type="number"
                    value={formData.fallback_priority}
                    onChange={(e) => setFormData({ ...formData, fallback_priority: parseInt(e.target.value) })}
                    className="bg-stone-800 border-stone-700 w-20"
                    min={1}
                    max={100}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CENTER PANEL - Editor */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Editor Header */}
          <div className="h-12 border-b border-stone-800 bg-stone-900/30 flex items-center justify-between px-4 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Code size={16} className="text-slate-500" />
              <span className="text-sm text-slate-400">Template Content</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">{formData.content?.length || 0} characters</span>
            </div>
          </div>
          
          {/* Description */}
          <div className="p-4 border-b border-stone-800 flex-shrink-0">
            <label className="text-xs text-slate-500 block mb-2">Description</label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this template..."
              className="bg-stone-800 border-stone-700"
            />
          </div>
          
          {/* Main Editor */}
          <div className="flex-1 p-4 overflow-hidden">
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Enter template content here...

Use Jinja2 syntax for placeholders:
{{ consumer.full_name }}
{{ bureau.name }}
{{ letter_date }}

Example:
Dear Consumer Relations Department,

I am writing to formally dispute information in my credit file maintained by {{ bureau.name }}..."
              className="w-full h-full bg-stone-900 border border-stone-800 rounded-lg p-4 text-white font-mono text-sm resize-none focus:outline-none focus:border-amber-500/50"
              style={{ minHeight: '400px' }}
            />
          </div>
        </div>

        {/* RIGHT PANEL - Preview & Placeholders */}
        <div className="w-96 border-l border-stone-800 bg-stone-900/50 flex flex-col flex-shrink-0">
          {/* Panel Tabs */}
          <div className="h-12 border-b border-stone-800 flex items-center px-4 flex-shrink-0">
            <button
              onClick={() => setShowPreview(true)}
              className={`px-3 py-1.5 text-sm rounded-lg mr-2 ${showPreview ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:text-white'}`}
            >
              <Eye size={14} className="inline mr-1" /> Preview
            </button>
            <button
              onClick={() => setShowPreview(false)}
              className={`px-3 py-1.5 text-sm rounded-lg ${!showPreview ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:text-white'}`}
            >
              <BookOpen size={14} className="inline mr-1" /> Placeholders
            </button>
          </div>

          {/* Panel Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {showPreview ? (
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-3">Live Preview</h4>
                {previewHtml ? (
                  <div 
                    className="bg-white text-black rounded-lg p-6 text-sm font-serif leading-relaxed"
                    style={{ 
                      fontFamily: "'Times New Roman', Times, serif",
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {previewHtml}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    Start typing to see preview
                  </div>
                )}
                
                {/* Routing Summary */}
                <div className="mt-6 space-y-3">
                  <h4 className="text-sm font-medium text-slate-300">Routing Summary</h4>
                  <div className="bg-stone-800/50 rounded-lg p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Method</span>
                      <span className="text-amber-400 font-mono">{formData.method_key || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Round</span>
                      <span className="text-white">R{formData.round}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Output</span>
                      <span className={formData.output_format === 'flat_legal' ? 'text-emerald-400' : 'text-amber-400'}>
                        {formData.output_format === 'flat_legal' ? 'Flat Legal' : formData.output_format}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Bureau Target</span>
                      <span className="text-white capitalize">{formData.bureau_targeting_mode?.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                </div>

                {/* Validation Warnings */}
                {!formData.name && (
                  <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-amber-400 text-sm">
                      <AlertTriangle size={14} />
                      <span>Template name is required</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-3">Available Placeholders</h4>
                <p className="text-xs text-slate-500 mb-4">Click to insert at cursor position</p>
                <div className="space-y-2">
                  {PLACEHOLDER_TAGS.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => insertPlaceholder(tag)}
                      className="w-full text-left px-3 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg text-sm text-teal-400 font-mono transition-colors"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


// ==================== MAIN PAGE ====================

const TemplateManagerFullPage = () => {
  const { token, getAuthHeaders } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  // Set auth headers when token changes
  useEffect(() => {
    if (token) {
      adminOpsService.setAuthHeaders(getAuthHeaders());
    }
  }, [token, getAuthHeaders]);

  const fetchTemplates = useCallback(async () => {
    if (!token) return;
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const response = await adminOpsService.listTemplates();
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [token, getAuthHeaders]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowEditor(true);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowEditor(true);
  };

  const handleSave = () => {
    setShowEditor(false);
    setEditingTemplate(null);
    fetchTemplates();
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (template) => {
    setTemplateToDelete(template);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;
    
    setDeleting(true);
    
    // Use XMLHttpRequest to avoid fetch stream issues with Emergent's monitoring
    // Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
    const xhr = new XMLHttpRequest();
    
    const promise = new Promise((resolve, reject) => {
      xhr.open('DELETE', `${BACKEND_URL}/api/admin-ops/templates/${templateToDelete.id}`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.withCredentials = true;
      
      xhr.onload = function() {
        let data = {};
        try {
          data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        } catch {
          data = { detail: xhr.responseText || `HTTP ${xhr.status}` };
        }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          reject({ status: xhr.status, data });
        }
      };
      
      xhr.onerror = function() {
        reject({ status: 0, data: { detail: 'Network error' } });
      };
      
      xhr.send();
    });
    
    try {
      const data = await promise;
      toast.success(data.message || `Template "${templateToDelete.name}" deleted successfully`);
      setDeleteModalOpen(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (err) {
      const { status, data } = err;
      // Extract the error message from the response
      const errorMessage = typeof data?.detail === 'string' 
        ? data.detail 
        : (data?.detail?.error || data?.detail?.message || data?.message || `HTTP ${status}`);
      
      // Provide more specific guidance based on error
      if (errorMessage.toLowerCase().includes('active') || errorMessage.toLowerCase().includes('system')) {
        toast.error(`Cannot delete: ${errorMessage}`, {
          description: 'Consider using deprecate or archive instead.',
          duration: 6000
        });
      } else if (errorMessage.toLowerCase().includes('routing') || errorMessage.toLowerCase().includes('in use')) {
        toast.error(`Cannot delete: ${errorMessage}`, {
          description: 'Remove from routing matrix first.',
          duration: 6000
        });
      } else {
        toast.error(errorMessage, { duration: 5000 });
      }
      console.error('Delete error:', data);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setTemplateToDelete(null);
  };

  const handleDuplicate = async (template) => {
    try {
      const duplicateData = {
        ...template,
        name: `${template.name} (Copy)`,
        status: 'draft',
      };
      delete duplicateData.id;
      delete duplicateData._id;
      await adminOpsService.createTemplate(duplicateData);
      toast.success('Template duplicated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const filteredTemplates = templates.filter((t) => {
    const matchesSearch = !searchQuery || 
      t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || t.template_type === filterType;
    const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  if (showEditor) {
    return (
      <TemplateEditor
        template={editingTemplate}
        onSave={handleSave}
        onClose={() => { setShowEditor(false); setEditingTemplate(null); }}
        authToken={token}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1F18] p-6" data-testid="template-manager-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-600/20 to-emerald-600/20 flex items-center justify-center">
              <FileType2 className="text-teal-400" size={20} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Template Manager</h1>
              <p className="text-sm text-slate-400">Edit template content, placeholders, and versioning</p>
            </div>
          </div>
          <Button onClick={handleCreate} className="bg-teal-600 hover:bg-teal-700">
            <Plus size={16} className="mr-2" /> New Template
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="pl-10 bg-stone-900 border-stone-800"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40 bg-stone-900 border-stone-800">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {TEMPLATE_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-stone-900 border-stone-800">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="deprecated">Deprecated</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={fetchTemplates} variant="outline" size="icon" className="border-stone-800">
          <RefreshCw size={16} />
        </Button>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-48 bg-stone-800 rounded-xl" />
          ))}
        </div>
      ) : filteredTemplates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onPreview={handleEdit}
              onDuplicate={handleDuplicate}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <FileType2 size={48} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-medium text-slate-400 mb-2">No templates found</h3>
          <p className="text-sm text-slate-500 mb-4">Get started by creating your first template</p>
          <Button onClick={handleCreate} className="bg-teal-600 hover:bg-teal-700">
            <Plus size={16} className="mr-2" /> Create Template
          </Button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && templateToDelete && (
        <div 
          className="fixed inset-0 bg-[rgba(6,28,20,0.82)] z-50 flex items-center justify-center p-4"
          onClick={cancelDelete}
          data-testid="delete-modal-overlay"
        >
          <div 
            className="bg-stone-900 border border-stone-700 rounded-xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-modal"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Trash2 className="text-red-400" size={20} />
              </div>
              <h2 className="text-lg font-semibold text-white">Delete Template</h2>
            </div>
            
            <div className="mb-6">
              <p className="text-slate-300 mb-3">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-white">"{templateToDelete.name}"</span>?
              </p>
              
              {templateToDelete.status === 'active' && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
                    <AlertTriangle size={14} />
                    <span>This is an active template</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Active templates may be in use. If deletion fails, consider using deprecate or archive instead.
                  </p>
                </div>
              )}
              
              <p className="text-sm text-slate-500">
                This action cannot be undone.
              </p>
            </div>
            
            <div className="flex items-center justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={cancelDelete}
                disabled={deleting}
                className="border-stone-700"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="delete-confirm-btn"
              >
                {deleting ? (
                  <>
                    <RefreshCw size={14} className="mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} className="mr-2" />
                    Delete Template
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManagerFullPage;
